import type { FieldErrors } from "react-hook-form";

interface FirstFormError {
  field: string;
  message: string;
}

export const getFirstFormError = (errors: FieldErrors, parentPath = ""): FirstFormError | null => {
  for (const key of Object.keys(errors)) {
    const error: any = errors[key];
    const fieldPath = parentPath ? `${parentPath}.${key}` : key;

    if (error?.message) {
      return { field: fieldPath, message: error.message as string };
    }

    if (Array.isArray(error)) {
      for (let i = 0; i < error.length; i++) {
        const nested = getFirstFormError(error[i], `${fieldPath}.${i}`);
        if (nested) return nested;
      }
    } else if (typeof error === "object") {
      const nested = getFirstFormError(error, fieldPath);
      if (nested) return nested;
    }
  }
  return null;
};

const FIELD_ID_PREFIXES = ["wo-field-", "visit-field-", "sp-field-"] as const;
const SCROLL_TOP_OFFSET = 24;
const WINDOW_SCROLL_TOP_OFFSET = 100;
const MAX_RETRY_FRAMES = 40;

export function findErrorElement(field: string): HTMLElement | null {
  const pathParts = field.split(".");
  const baseKey = pathParts[0];
  const leafKey = pathParts[pathParts.length - 1];

  const candidates = Array.from(new Set([field, leafKey, baseKey]));

  for (const key of candidates) {
    for (const prefix of FIELD_ID_PREFIXES) {
      const byId = document.getElementById(`${prefix}${key}`);
      if (byId) return byId;
    }

    const byName = document.querySelector<HTMLElement>(`[name="${key}"]`);
    if (byName) return byName;
  }

  return null;
}

function findScrollableAncestor(el: HTMLElement): HTMLElement | null {
  let current: HTMLElement | null = el.parentElement;
  while (current && current !== document.documentElement) {
    const { overflowY } = window.getComputedStyle(current);
    if (
      (overflowY === "auto" || overflowY === "scroll") &&
      current.scrollHeight > current.clientHeight
    ) {
      return current;
    }
    current = current.parentElement;
  }
  return null;
}

export function scrollElementIntoView(el: HTMLElement): void {
  const container = findScrollableAncestor(el);
  if (!container) {
    const rect = el.getBoundingClientRect();
    const currentScrollY = window.scrollY || document.documentElement.scrollTop;

    if (rect.top < WINDOW_SCROLL_TOP_OFFSET) {
      window.scrollTo({
        top: Math.max(0, currentScrollY + rect.top - WINDOW_SCROLL_TOP_OFFSET),
        behavior: "smooth",
      });
    } else if (rect.bottom > window.innerHeight) {
      window.scrollTo({
        top: currentScrollY + rect.bottom - window.innerHeight + SCROLL_TOP_OFFSET,
        behavior: "smooth",
      });
    }
    return;
  }

  const elRect = el.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();
  const elTopInContainer = elRect.top - containerRect.top + container.scrollTop;
  const maxScroll = container.scrollHeight - container.clientHeight;
  const targetScroll = Math.max(0, Math.min(maxScroll, elTopInContainer - SCROLL_TOP_OFFSET));

  container.scrollTo({ top: targetScroll, behavior: "smooth" });
}

function isElementUsable(el: HTMLElement): boolean {
  if (!el.isConnected) return false;
  if (el.offsetParent === null && getComputedStyle(el).position !== "fixed") return false;
  const rect = el.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
}

function focusAndScroll(fieldName: string, setFocus?: (name: any) => void): void {
  setFocus?.(fieldName);

  requestAnimationFrame(() => {
    const el =
      findErrorElement(fieldName) ??
      (document.activeElement instanceof HTMLElement && document.activeElement !== document.body
        ? document.activeElement
        : null);
    if (el) scrollElementIntoView(el);
  });
}

export const FORM_VALIDATION_FAILED_EVENT = "form:validation-failed";

interface ValidationFailedDetail {
  fieldName: string;
  errors?: FieldErrors;
}

export function dispatchValidationFailed(detail: ValidationFailedDetail): void {
  if (typeof window === "undefined" || !detail?.fieldName) return;
  window.dispatchEvent(new CustomEvent(FORM_VALIDATION_FAILED_EVENT, { detail }));
}

export function focusFieldWithRetry(
  fieldName: string,
  setFocus?: (name: any) => void,
  errors?: FieldErrors,
): void {
  if (!fieldName) return;
  dispatchValidationFailed({ fieldName, errors });

  let attemptsLeft = MAX_RETRY_FRAMES;
  const attempt = () => {
    const el = findErrorElement(fieldName);
    if (el && isElementUsable(el)) {
      focusAndScroll(fieldName, setFocus);
      return;
    }
    if (--attemptsLeft > 0) {
      requestAnimationFrame(attempt);
      return;
    }
    focusAndScroll(fieldName, setFocus);
  };
  requestAnimationFrame(attempt);
}

export function scrollToFirstError(
  errors: FieldErrors,
  setFocus?: (name: any) => void,
  fieldOrder?: string[],
): void {
  let firstErrorField: string | undefined;

  if (fieldOrder) {
    firstErrorField = fieldOrder.find((field) => errors[field] != null);
  } else {
    const firstError = getFirstFormError(errors);
    firstErrorField = firstError?.field;
  }

  if (!firstErrorField) return;

  focusFieldWithRetry(firstErrorField, setFocus, errors);
}
